import {formatNumber, NumberType} from '@/components/charts/numbers';
import {Chart, LegendItem, ScatterDataPoint, ScriptableContext, TimeUnit, TooltipItem} from 'chart.js';
import 'chartjs-adapter-moment';

const MILESTONE_LEGEND_LABEL = 'Nová nemovitost v portfoliu';
const MILESTONE_POINT_RADIUS = 5;

export default class LineChart {
    private chart: Chart;

    public constructor(
        element: HTMLCanvasElement,
        axisYTitle: string,
        dataSets: ChartDataSet[],
        period: TimeUnit,
        numberType: NumberType,
        language: string,
        currency: string,
        legend: boolean,
    ) {
        this.chart = new Chart(
            element,
            {
                type: 'line',
                data: {
                    datasets: dataSets.map(dataSet => ({
                        ...dataSet,
                        borderWidth: 2.5,
                        // only milestones are drawn as points — a ring in the line color
                        pointRadius: (context: ScriptableContext<'line'>) => LineChart.isMilestone(dataSet, context)
                            ? MILESTONE_POINT_RADIUS
                            : 0,
                        pointBorderWidth: 2.5,
                        pointBackgroundColor: '#fff',
                        pointBorderColor: dataSet.borderColor,
                        pointHoverRadius: (context: ScriptableContext<'line'>) => LineChart.isMilestone(dataSet, context)
                            ? MILESTONE_POINT_RADIUS + 2
                            : 4,
                        pointHoverBorderWidth: 2.5,
                        pointHoverBackgroundColor: '#fff',
                        // filled datasets get a soft vertical gradient under the line
                        backgroundColor: dataSet.fill
                            ? (context: ScriptableContext<'line'>) => this.createAreaGradient(context, dataSet.borderColor)
                            : dataSet.backgroundColor,
                    })),
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                        x: {
                            type: 'time',
                            time: {
                                // the unit adapts to the charted span (years for long
                                // series), it only never goes finer than the data period
                                minUnit: LineChart.toTimeUnit(period),
                                displayFormats: {
                                    month: 'MM/YY',
                                    quarter: '[Q]Q YYYY',
                                    year: 'YYYY',
                                },
                            },
                            grid: {
                                display: false,
                            },
                            ticks: {
                                maxRotation: 0,
                                autoSkipPadding: 16,
                                maxTicksLimit: 12,
                            },
                        },
                        y: {
                            type: 'linear',
                            title: {
                                text: axisYTitle,
                                font: {
                                    weight: 'bold',
                                },
                                display: axisYTitle !== '',
                            },
                            grid: {
                                color: 'rgba(148, 163, 184, 0.18)',
                                borderDash: [4, 4],
                                drawBorder: false,
                            },
                            ticks: {
                                callback: numberValue => formatNumber(
                                    typeof numberValue === 'string'
                                        ? parseInt(numberValue, 1)
                                        : numberValue,
                                    numberType,
                                    language,
                                    currency,
                                ),
                            },
                            beginAtZero: true,
                        },
                    },
                    plugins: {
                        legend: {
                            display: legend,
                            position: 'bottom',
                            labels: {
                                usePointStyle: true,
                                generateLabels: chart => LineChart.legendLabels(chart, dataSets),
                            },
                            onClick(event, legendItem, legendInstance) {
                                // the milestone symbol explains the marks, it toggles nothing
                                if (legendItem.datasetIndex < 0) {
                                    return;
                                }

                                Chart.defaults.plugins.legend.onClick.call(this, event, legendItem, legendInstance);
                            },
                        },
                        tooltip: {
                            mode: 'index',
                            intersect: false,
                            position: 'nearest',
                            bodySpacing: 5,
                            enabled: true,
                            callbacks: {
                                title: items => items.length > 0
                                    ? LineChart.formatTooltipTitle(items[0].parsed.x, period, language)
                                    : '',
                                label(item: any) {
                                    return item.dataset.label + ': '
                                        + formatNumber(item.raw[1], numberType, language, currency);
                                },
                                afterBody: items => LineChart.milestonesAt(dataSets, items).length > 0
                                    ? ['', MILESTONE_LEGEND_LABEL]
                                    : [],
                                footer: items => LineChart.milestonesAt(dataSets, items).flatMap(
                                    milestone => milestone.note === null
                                        ? [milestone.title]
                                        : [milestone.title, milestone.note],
                                ),
                            },
                            footerFont: {
                                weight: 'normal',
                            },
                            footerColor: 'rgba(255, 255, 255, 0.72)',
                            footerMarginTop: 4,
                        },
                    },
                    interaction: {
                        mode: 'index',
                        intersect: false,
                    },
                    hover: {
                        mode: 'nearest',
                        intersect: true,
                    },
                },
                plugins: [LineChart.milestoneLinesPlugin(dataSets)],
            },
        );
    }

    /**
     * A thin dashed guide from the axis up to every milestone point, drawn
     * under the lines so the point itself stays the focus.
     */
    private static milestoneLinesPlugin(dataSets: ChartDataSet[]) {
        return {
            id: 'milestoneLines',
            beforeDatasetsDraw(chart: Chart) {
                const {ctx, chartArea} = chart;

                ctx.save();
                ctx.setLineDash([3, 3]);
                ctx.lineWidth = 1;

                dataSets.forEach((dataSet, datasetIndex) => {
                    if (!chart.isDatasetVisible(datasetIndex)) {
                        return;
                    }

                    const meta = chart.getDatasetMeta(datasetIndex);

                    dataSet.data.forEach((point, index) => {
                        if (!LineChart.milestonesOf(dataSet, LineChart.pointDate(point)).length) {
                            return;
                        }

                        const element = meta.data[index];

                        if (!element) {
                            return;
                        }

                        ctx.strokeStyle = dataSet.borderColor.replace('rgb(', 'rgba(').replace(')', ', 0.55)');
                        ctx.beginPath();
                        ctx.moveTo(element.x, chartArea.bottom);
                        ctx.lineTo(element.x, element.y + MILESTONE_POINT_RADIUS);
                        ctx.stroke();
                    });
                });

                ctx.restore();
            },
        };
    }

    private static legendLabels(chart: Chart, dataSets: ChartDataSet[]): LegendItem[] {
        const labels = Chart.defaults.plugins.legend.labels.generateLabels(chart);
        const marked = dataSets.find(dataSet => (dataSet.milestones ?? []).length > 0);

        if (marked === undefined) {
            return labels;
        }

        return [
            ...labels,
            {
                text: MILESTONE_LEGEND_LABEL,
                pointStyle: 'circle',
                fillStyle: '#fff',
                strokeStyle: marked.borderColor,
                lineWidth: 2,
                hidden: false,
                datasetIndex: -1,
            },
        ];
    }

    private static isMilestone(dataSet: ChartDataSet, context: ScriptableContext<'line'>): boolean {
        const point = dataSet.data[context.dataIndex];

        return point !== undefined && LineChart.milestonesOf(dataSet, LineChart.pointDate(point)).length > 0;
    }

    private static milestonesAt(dataSets: ChartDataSet[], items: TooltipItem<'line'>[]): ChartMilestone[] {
        if (items.length === 0) {
            return [];
        }

        const date = LineChart.pointDate(items[0].raw as ScatterDataPoint);

        return dataSets.flatMap(dataSet => LineChart.milestonesOf(dataSet, date));
    }

    /**
     * The points arrive as [date, value] tuples (see DataSet::getData()); the
     * milestones are matched on that date string, not on a timestamp, which
     * would depend on the timezone the adapter parses the date in.
     */
    private static pointDate(point: ScatterDataPoint): string {
        return (point as unknown as [string, number])[0];
    }

    private static milestonesOf(dataSet: ChartDataSet, date: string): ChartMilestone[] {
        return (dataSet.milestones ?? []).filter(milestone => milestone.x === date);
    }

    private static toTimeUnit(period: string): TimeUnit {
        return (['month', 'quarter', 'year'].includes(period) ? period : 'month') as TimeUnit;
    }

    private static formatTooltipTitle(timestamp: number, period: string, language: string): string {
        const date = new Date(timestamp);

        if (period === 'quarter') {
            return `${Math.floor(date.getMonth() / 3) + 1}. čtvrtletí ${date.getFullYear()}`;
        }

        if (period === 'year') {
            return String(date.getFullYear());
        }

        // Intl (unlike the moment cs locale) uses the correct nominative
        // case for standalone month names
        return new Intl.DateTimeFormat(language, {month: 'long', year: 'numeric'}).format(date);
    }

    private createAreaGradient(context: ScriptableContext<'line'>, borderColor: string): CanvasGradient|string {
        const chartArea = context.chart.chartArea;

        if (!chartArea) {
            // the chart area is unknown before the first layout pass
            return 'transparent';
        }

        const gradient = context.chart.ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        gradient.addColorStop(0, this.withOpacity(borderColor, 0.22));
        gradient.addColorStop(1, this.withOpacity(borderColor, 0));

        return gradient;
    }

    private withOpacity(rgbColor: string, opacity: number): string {
        return rgbColor.replace('rgb(', 'rgba(').replace(')', `, ${opacity})`);
    }
}

export interface ChartDataSet {
    label: string;
    backgroundColor: string;
    borderColor: string;
    color: string;
    data: ScatterDataPoint[];
    lineTension: number;
    fill?: boolean;
    milestones?: ChartMilestone[];
}

export interface ChartMilestone {
    x: string;
    title: string;
    note: string|null;
}
